import { realtimeService } from '@peritasai/data-access';
import {
  AssetEntity,
  getChildLocations,
  LocationEntity,
  useAllLocations,
} from '@peritasai/repository';
import {
  AssetType,
  AssetUpdateEventFilters,
  RealtimeEventType,
  type EventSubscription,
} from '@peritasai/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DetectedAsset } from './types';

type AssignBatchResult = { ok: boolean };

export type DiscoveryConfig = {
  /** True while the hub should be actively scanning (the progress step). */
  enabled: boolean;
  /** True when the flow is on its success screen (preserves the success timeout on cleanup). */
  isSuccess: boolean;
  selectedLocation?: LocationEntity | null;
  selectedCaseId?: string | null;
  /** Asset types accepted from the realtime stream. Defaults to trays. */
  discoverableAssetTypes?: readonly AssetType[];
  getAssetsForLocation: (locationId: string) => Promise<AssetEntity[]>;
  assignTrackedItemsBatch: (payload: {
    caseId: string;
    assetIds: string[];
    firstCartAssetId?: string;
  }) => Promise<AssignBatchResult>;
  /** The hub could not be reached / stream failed. */
  onHubOffline: () => void;
  /** A batch assign succeeded — show the success screen. */
  onAssignSuccess: () => void;
  /** The success screen has been shown long enough — return to case selection. */
  onAssignSettled: () => void;
  /** User cancelled discovery — return to case selection. */
  onCancel: () => void;
};

export type DiscoveryResult = {
  detectedAssets: DetectedAsset[];
  resetDiscoverySession: () => void;
  handleCancelDiscovery: () => void;
  handleToggleAsset: (assetId: string) => void;
  handleAssign: () => void;
  isAssignLoading: boolean;
};

type ResetDiscoverySessionOptions = {
  preserveAssignSuccessTimeout?: boolean;
};

type DetectedAssetSource = {
  id: string;
  name: string;
  subtitle: string;
  assetType?: AssetType;
  detectedAt?: Date | string | null;
};

const formatDetectedAtLabel = (timestamp?: Date | string | null) => {
  const detectedAt = timestamp ? new Date(timestamp) : new Date();
  const timeLabel = detectedAt
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/\s/g, '')
    .toLowerCase();
  return `${timeLabel} • Detected`;
};

const toDetectedAsset = (
  source: DetectedAssetSource,
  selected: boolean
): DetectedAsset => ({
  id: source.id,
  name: source.name,
  subtitle: source.subtitle,
  assetType: source.assetType,
  detectedAtLabel: formatDetectedAtLabel(source.detectedAt),
  selected,
});

const RECENT_DETECTION_WINDOW_MS = 5 * 60 * 1000;
const ASSIGN_SUCCESS_DISPLAY_MS = 1200;
const DEFAULT_DISCOVERABLE_ASSET_TYPES = [AssetType.TRAY];

export const useDiscovery = ({
  enabled,
  isSuccess,
  selectedLocation,
  selectedCaseId,
  discoverableAssetTypes = DEFAULT_DISCOVERABLE_ASSET_TYPES,
  getAssetsForLocation,
  assignTrackedItemsBatch,
  onHubOffline,
  onAssignSuccess,
  onAssignSettled,
  onCancel,
}: DiscoveryConfig): DiscoveryResult => {
  const [detectedAssets, setDetectedAssets] = useState<DetectedAsset[]>([]);
  const [isAssignLoading, setIsAssignLoading] = useState<boolean>(false);

  // Keep the latest callbacks in a ref so the scan/stream effect below doesn't
  // re-run (and reconnect the stream) when a caller passes inline callbacks.
  const callbacksRef = useRef({
    getAssetsForLocation,
    onHubOffline,
    onAssignSuccess,
    onAssignSettled,
    onCancel,
  });
  callbacksRef.current = {
    getAssetsForLocation,
    onHubOffline,
    onAssignSuccess,
    onAssignSettled,
    onCancel,
  };
  const startScanRequestInFlightRef = useRef(false);
  const streamAbortRef = useRef<AbortController | null>(null);
  const streamErrorCountRef = useRef(0);
  const isAssigningRef = useRef(false);
  const assignSuccessTimeoutRef = useRef<number | null>(null);

  const locationQuery = useAllLocations();

  const clearActiveStream = useCallback(() => {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
  }, []);

  const clearAssignSuccessTimeout = useCallback(() => {
    if (assignSuccessTimeoutRef.current && typeof window !== 'undefined') {
      window.clearTimeout(assignSuccessTimeoutRef.current);
      assignSuccessTimeoutRef.current = null;
    }
  }, []);

  const resetDiscoverySession = useCallback(
    ({
      preserveAssignSuccessTimeout = false,
    }: ResetDiscoverySessionOptions = {}) => {
      if (!preserveAssignSuccessTimeout) {
        clearAssignSuccessTimeout();
      }
      clearActiveStream();
      setDetectedAssets([]);
      startScanRequestInFlightRef.current = false;
      streamErrorCountRef.current = 0;
      isAssigningRef.current = false;
    },
    [clearActiveStream, clearAssignSuccessTimeout]
  );

  const handleCancelDiscovery = useCallback(() => {
    resetDiscoverySession();
    callbacksRef.current.onCancel();
  }, [resetDiscoverySession]);

  const handleToggleAsset = useCallback((assetId: string) => {
    setDetectedAssets((prev) => {
      const target = prev.find((a) => a.id === assetId);
      const isSelectingCart =
        target && !target.selected && target.assetType === AssetType.CASE_CART;
      return prev.map((a) => {
        if (a.id === assetId) return { ...a, selected: !a.selected };
        if (isSelectingCart && a.assetType === AssetType.CASE_CART)
          return { ...a, selected: false };
        return a;
      });
    });
  }, []);

  const handleAssign = useCallback(() => {
    if (isAssigningRef.current) return;
    if (!selectedCaseId) return;

    const selectedAssets = detectedAssets.filter((asset) => asset.selected);
    if (selectedAssets.length === 0) return;

    isAssigningRef.current = true;
    const firstCartAsset = selectedAssets.find(
      (asset) => asset.assetType === AssetType.CASE_CART
    );
    const assetIds = selectedAssets
      .filter((asset) => asset.assetType !== AssetType.CASE_CART)
      .map((asset) => asset.id);
    if (assetIds.length === 0) {
      isAssigningRef.current = false;
      return;
    }

    setIsAssignLoading(true);
    void assignTrackedItemsBatch({
      caseId: selectedCaseId,
      assetIds,
      firstCartAssetId: firstCartAsset?.id,
    })
      .then((result) => {
        if (!result.ok) {
          console.error('Assign tracked items failed', result);
          return;
        }
        callbacksRef.current.onAssignSuccess();
        assignSuccessTimeoutRef.current = window.setTimeout(() => {
          assignSuccessTimeoutRef.current = null;
          resetDiscoverySession();
          callbacksRef.current.onAssignSettled();
        }, ASSIGN_SUCCESS_DISPLAY_MS);
      })
      .finally(() => {
        isAssigningRef.current = false;
        setIsAssignLoading(false);
      });
  }, [
    assignTrackedItemsBatch,
    detectedAssets,
    resetDiscoverySession,
    selectedCaseId,
  ]);

  const locationId = selectedLocation?.id ?? null;
  const locationChildIds = useMemo(
    () =>
      selectedLocation
        ? [
            selectedLocation,
            ...getChildLocations(locationQuery.data, selectedLocation?.id),
          ].map((loc) => loc?.id)
        : [],
    [locationQuery.data, selectedLocation]
  );
  const assetTypeFilter = discoverableAssetTypes.join(',');

  useEffect(() => {
    let isActive = true;

    if (!locationId) {
      resetDiscoverySession();
      return;
    }

    if (!enabled) {
      resetDiscoverySession({ preserveAssignSuccessTimeout: isSuccess });
      return;
    }

    const startScan = () => {
      if (startScanRequestInFlightRef.current) return;
      startScanRequestInFlightRef.current = true;

      void callbacksRef.current
        .getAssetsForLocation(locationId)
        .then((result) => {
          if (!isActive) return;
          const now = Date.now();
          setDetectedAssets((prev: DetectedAsset[]) =>
            result
              .filter((item: AssetEntity) => {
                if (!item.locationUpdatedAt) return false;
                const diff = Math.abs(now - item.locationUpdatedAt.getTime());
                return diff <= RECENT_DETECTION_WINDOW_MS;
              })
              .map((asset: AssetEntity) =>
                toDetectedAsset(
                  {
                    id: asset.id,
                    name: asset.name,
                    subtitle: asset.referenceNumber,
                    assetType: asset.type,
                    detectedAt: asset.locationUpdatedAt,
                  },
                  prev.find((item) => item.id === asset.id)?.selected ??
                    asset.caseId == null
                )
              )
          );
          startScanRequestInFlightRef.current = false;
        })
        .catch(() => {
          if (!isActive) return;
          startScanRequestInFlightRef.current = false;
          callbacksRef.current.onHubOffline();
        });
    };

    if (streamAbortRef.current) return;

    const filter: AssetUpdateEventFilters = {
      type: assetTypeFilter,
    };
    const subscriptions: EventSubscription[] = [
      {
        eventType: RealtimeEventType.ASSET_LOCATION_UPDATED,
        filters: filter,
      },
    ];

    const controller = new AbortController();
    streamAbortRef.current = controller;
    streamErrorCountRef.current = 0;

    void realtimeService.connect({
      subscriptions,
      signal: controller.signal,
      onEvent(event) {
        if (!event || event.type !== RealtimeEventType.ASSET_LOCATION_UPDATED) {
          return;
        }
        streamErrorCountRef.current = 0;
        const assetData = event.data.asset;
        if (
          !assetData.location?.id ||
          !locationChildIds.includes(assetData.location?.id) ||
          !discoverableAssetTypes.includes(assetData.type)
        ) {
          return;
        }

        setDetectedAssets((prev: DetectedAsset[]) => {
          const newAsset = toDetectedAsset(
            {
              id: assetData.id,
              name: assetData.name ?? 'Detected asset',
              subtitle: assetData.referenceNumber ?? assetData.id,
              assetType: assetData.type,
              detectedAt: assetData.locationUpdatedAt,
            },
            prev.find((item) => item.id === assetData.id)?.selected ??
              assetData.caseId == null
          );
          return [...prev.filter((item) => item.id !== newAsset.id), newAsset];
        });
      },
      onStateChange(state) {
        if (controller.signal.aborted) return;
        if (state.connected) {
          streamErrorCountRef.current = 0;
          startScan();
          return;
        }
        streamAbortRef.current = null;

        if (!state.error) {
          callbacksRef.current.onHubOffline();
          return;
        }

        const errorMessage = state.error.message ?? '';
        const isAbortError =
          state.error.name === 'AbortError' || /abort/i.test(errorMessage);
        const is401 = /401|Unauthorized/i.test(errorMessage);
        if (isAbortError || is401) return;

        const isUnexpectedClose = /stream closed unexpectedly/i.test(
          errorMessage
        );
        if (isUnexpectedClose) {
          callbacksRef.current.onHubOffline();
          return;
        }

        streamErrorCountRef.current += 1;
        if (streamErrorCountRef.current < 3) return;

        console.error('Discovery SSE error', state.error);
        callbacksRef.current.onHubOffline();
      },
    });

    return () => {
      clearActiveStream();
      isActive = false;
    };
  }, [
    assetTypeFilter,
    clearActiveStream,
    discoverableAssetTypes,
    enabled,
    isSuccess,
    locationId,
    resetDiscoverySession,
  ]);

  useEffect(() => {
    return () => {
      resetDiscoverySession();
    };
  }, [resetDiscoverySession]);

  return {
    detectedAssets,
    resetDiscoverySession,
    handleCancelDiscovery,
    handleToggleAsset,
    handleAssign,
    isAssignLoading,
  };
};
